import React, { useContext, useEffect, useState } from 'react';
import i18n from '@/utils/i18n';
import {
  GetCustomerCateringUsersForStoreDocument,
  useGetCustomerCateringUsersForStoreQuery,
} from '../../graphql/queries/getCustomerCateringUsersForStore.generated';
import { CustomTable } from '../../components/Table';
import CustomPagination from '../../components/CustomPagination';
import { CustomModal } from '../../components/CustomModal';
import {
  FaCheck,
  FaDownload,
  FaExchangeAlt,
  FaPlus,
  FaQrcode,
  FaUnlink,
} from 'react-icons/fa';
import { isNullOrWhitespace } from '@/utils/stringHelper';
import { useCreateCustomerCateringUserMutation } from '../../graphql/mutations/createCustomerCateringUser.generated';
import { Button, Input } from 'reactstrap';
import { useUnlinkCustomerCateringUserFromStoreMutation } from '../../graphql/mutations/unlinkCustomerCateringUserFromStore.generated';
import {
  CanAccessPartnerData,
  CanAccessUserData,
  IsVytalEmployee,
  ViewerContext,
} from '../../providers/ViewerContextProvider';
import { Link } from 'react-router-dom';
import { OverlayPopover } from '../../components/OverlayPopover';
import { getUserQrCodeImgUrl } from '@/utils/qrCode';
import { useTransferCustomerCateringUserContainersToStoreMutation } from '../../graphql/mutations/transferCustomerCateringUserContainersToStore.generated';
import { LayoutStoreInstance } from '../../LayoutStore';
import { downloadImage } from '@/utils/download';
import { ContentSection } from '../../components/Layout/ContentSection';

const PAGE_SIZE = 20;

type CustomerCateringUserListTabProps = {
  storeTextId: string;
  title: string;
};

export function CustomerCateringUserListTab({
  storeTextId,
  title,
}: CustomerCateringUserListTabProps) {
  const [page, setPage] = useState(1);
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [checkedUserIds, setCheckedUserIds] = useState<string[]>([]);
  const [createCateringCustomerModalOpen, setCreateCateringCustomerModalOpen] =
    useState(false);
  const [confirmUnlinkModalOpen, setConfirmUnlinkModalOpen] = useState(false);
  const [confirmTransferModalOpen, setConfirmTransferModalOpen] =
    useState(false);
  const viewer = useContext(ViewerContext);
  const canAccessUserData = CanAccessUserData(viewer);
  const canAccessPartnerData = CanAccessPartnerData(viewer);

  const { data, loading, error, refetch } =
    useGetCustomerCateringUsersForStoreQuery({
      nextFetchPolicy: 'no-cache',
      variables: {
        store_text_id: storeTextId,
        skip: 0,
        take: PAGE_SIZE,
      },
    });

  useEffect(() => {
    refetch({
      store_text_id: storeTextId,
      skip: (page - 1) * PAGE_SIZE,
      take: PAGE_SIZE,
    });
  }, [page]);

  const [createCustomerCateringUser] = useCreateCustomerCateringUserMutation({
    variables: {
      payload: {
        store_text_id: storeTextId,
        user_name: newUserName,
        user_email: newUserEmail,
      },
    },
    refetchQueries: [
      {
        query: GetCustomerCateringUsersForStoreDocument,
        variables: {
          store_text_id: storeTextId,
          skip: (page - 1) * PAGE_SIZE,
          take: PAGE_SIZE,
        },
      },
    ],
    onError: (e) => {
      console.log(e);
      let errorMessage = i18n.t('CustomerCateringUser:addError');
      if (IsVytalEmployee(viewer)) {
        errorMessage += `: ${e.message.replace(/_/g, ' ')}`;
      }
      LayoutStoreInstance.setError(errorMessage);
    },
  });

  const [unlinkCustomerCateringUsers] =
    useUnlinkCustomerCateringUserFromStoreMutation({
      refetchQueries: [
        {
          query: GetCustomerCateringUsersForStoreDocument,
          variables: {
            store_text_id: storeTextId,
            skip: (page - 1) * PAGE_SIZE,
            take: PAGE_SIZE,
          },
        },
      ],
      onError: (e) => {
        LayoutStoreInstance.setError(e.message.replace(/_/g, ' '));
      },
      onCompleted: () => {
        setCheckedUserIds([]);
      },
    });

  const [transferContainers] =
    useTransferCustomerCateringUserContainersToStoreMutation({
      refetchQueries: [
        {
          query: GetCustomerCateringUsersForStoreDocument,
          variables: {
            store_text_id: storeTextId,
            skip: (page - 1) * PAGE_SIZE,
            take: PAGE_SIZE,
          },
        },
      ],
      onError: (e) => {
        LayoutStoreInstance.setError(e.message.replace(/_/g, ' '));
      },
      onCompleted: () => {
        setCheckedUserIds([]);
      },
    });

  function getUserCell(user: any) {
    return (
      <>
        {canAccessUserData ? (
          <Link to={`/users/${user.user_uuid}`}>{user.user_name}</Link>
        ) : (
          user.user_name
        )}
        <OverlayPopover
          body={
            <img
              width={'100%'}
              alt="QR code for user"
              src={getUserQrCodeImgUrl(user.user_uuid)}
            />
          }
          header={i18n.t('CustomerCateringUser:qrCode')}
        >
          <FaQrcode className="ms-1" />
        </OverlayPopover>
      </>
    );
  }

  const total = data?.getCustomerCateringUsersForStore.total || 0;
  const totalPages = total === 0 ? 0 : Math.ceil(total / PAGE_SIZE);
  return (
    <ContentSection title={title} collapsable={false} key="content">
      <h4>{i18n.t('Common:customerCateringUsers')}</h4>
      <div>
        <Button
          className="float-end ms-2 mt-3 mb-3"
          onClick={() => setCreateCateringCustomerModalOpen(true)}
        >
          <FaPlus className="me-1" />
          {i18n.t('CustomerCateringUser:create')}
        </Button>
        {canAccessUserData && (
          <>
            <Button
              disabled={checkedUserIds.length === 0}
              className="float-end ms-2 mt-3 mb-3"
              onClick={() => setConfirmUnlinkModalOpen(true)}
            >
              <FaUnlink className="me-1" />
              {i18n.t('CustomerCateringUser:unlink')}
            </Button>
            <Button
              disabled={checkedUserIds.length === 0}
              className="float-end ms-2 mt-3 mb-3"
              onClick={() => setConfirmTransferModalOpen(true)}
            >
              <FaExchangeAlt className="me-1" />
              {i18n.t('CustomerCateringUser:transfer')}
            </Button>
          </>
        )}
      </div>
      <CustomTable
        error={error}
        headers={[
          ...(canAccessPartnerData ? [i18n.t('Common:select')] : []),
          i18n.t('CustomerCateringUser:userName'),
          i18n.t('CustomerCateringUser:email'),
          i18n.t('CustomerCateringUser:containers'),
          '',
          i18n.t('CustomerCateringUser:downloadQrCode'),
        ]}
        hover
        loading={loading}
        rows={
          data?.getCustomerCateringUsersForStore.users.map((user) => (
            <tr key={user.user_uuid}>
              {canAccessPartnerData && (
                <td className="text-center align-middle">
                  <Input
                    type="checkbox"
                    checked={checkedUserIds.includes(user.user_uuid)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setCheckedUserIds([...checkedUserIds, user.user_uuid]);
                      } else {
                        setCheckedUserIds(
                          checkedUserIds.filter((id) => id !== user.user_uuid),
                        );
                      }
                    }}
                  ></Input>
                </td>
              )}
              <td className="align-middle">{getUserCell(user)}</td>
              <td className="align-middle">{user.user_email}</td>
              <td colSpan={2}>
                <CustomTable
                  error={undefined}
                  loading={false}
                  headers={[
                    i18n.t('CustomerCateringUser:containerType'),
                    i18n.t('CustomerCateringUser:amount'),
                  ]}
                  hover
                  rows={[
                    ...(user.container_amounts.map((containerAmount) => (
                      <tr key={containerAmount.container_type_id}>
                        <td>
                          <img
                            className="me-2"
                            alt=""
                            style={{ width: '30px' }}
                            src={
                              containerAmount.container_type_image || undefined
                            }
                          />
                          {containerAmount.container_type_name}
                        </td>
                        <td>{containerAmount.amount}</td>
                      </tr>
                    )) || []),
                    <tr key={`${user.user_uuid}-total`}>
                      <td>{i18n.t('CustomerCateringUser:total')}</td>
                      <td>
                        {user.container_amounts.reduce(
                          (acc, curr) => acc + curr.amount,
                          0,
                        )}
                      </td>
                    </tr>,
                  ]}
                  striped
                  bordered
                ></CustomTable>
              </td>
              <td className="text-center align-middle">
                {
                  <Button
                    onClick={() =>
                      downloadImage(
                        getUserQrCodeImgUrl(user.user_uuid),
                        `${user.user_name}_qr_code.png`,
                      )
                    }
                  >
                    <FaDownload />
                  </Button>
                }
              </td>
            </tr>
          )) || []
        }
        striped
      ></CustomTable>
      <CustomPagination
        currentPage={page}
        pageCount={totalPages}
        onPageChange={setPage}
      />
      <CustomModal
        acceptButtonIcon={<FaPlus />}
        acceptButtonText={i18n.t('Common:create')}
        header={i18n.t('CustomerCateringUser:createModalTitle')}
        isOpen={createCateringCustomerModalOpen}
        setIsOpen={setCreateCateringCustomerModalOpen}
        acceptButtonAction={createCustomerCateringUser}
        acceptButtonDisabled={
          isNullOrWhitespace(newUserName) || isNullOrWhitespace(newUserEmail)
        }
        body={
          <>
            <span>{i18n.t('CustomerCateringUser:createModalDescription')}</span>
            <div className="input-group mt-3 mb-3">
              <div className="input-group-prepend">
                <span className="input-group-text">
                  {i18n.t('CustomerCateringUser:userName')}
                </span>
              </div>
              <input
                className="form-control"
                type="text"
                onChange={(e) => setNewUserName(e.target.value)}
                value={newUserName}
              />
            </div>
            <div className="input-group mb-3">
              <div className="input-group-prepend">
                <span className="input-group-text">
                  {i18n.t('CustomerCateringUser:userEmail')}
                </span>
              </div>
              <input
                className="form-control"
                type="text"
                onChange={(e) => setNewUserEmail(e.target.value)}
                value={newUserEmail}
              />
            </div>
          </>
        }
      />
      <CustomModal
        acceptButtonAction={async () => {
          unlinkCustomerCateringUsers({
            variables: {
              user_uuids: checkedUserIds,
            },
          });
        }}
        acceptButtonDisabled={false}
        acceptButtonIcon={<FaCheck />}
        acceptButtonText={i18n.t('Common:confirm')}
        body={i18n.t('CustomerCateringUser:confirmUnlinkModalText', {
          amount: checkedUserIds.length,
        })}
        header={i18n.t('CustomerCateringUser:confirmUnlinkModalTitle')}
        isOpen={confirmUnlinkModalOpen}
        setIsOpen={setConfirmUnlinkModalOpen}
      />
      <CustomModal
        acceptButtonAction={async () => {
          transferContainers({
            variables: {
              user_uuids: checkedUserIds,
            },
          });
        }}
        acceptButtonDisabled={false}
        acceptButtonIcon={<FaCheck />}
        acceptButtonText={i18n.t('Common:confirm')}
        body={i18n.t('CustomerCateringUser:confirmTransferModalText', {
          userAmount: checkedUserIds.length,
          containerAmount: checkedUserIds.reduce(
            (acc, curr) =>
              acc +
              (data?.getCustomerCateringUsersForStore.users
                .find((user) => user.user_uuid === curr)
                ?.container_amounts.reduce(
                  (acc, curr) => acc + curr.amount,
                  0,
                ) || 0),
            0,
          ),
        })}
        header={i18n.t('CustomerCateringUser:confirmTransferModalTitle')}
        isOpen={confirmTransferModalOpen}
        setIsOpen={setConfirmTransferModalOpen}
      />
    </ContentSection>
  );
}

export default CustomerCateringUserListTab;
